import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Tooltip,
  Select,
  message,
  Switch,
  Typography,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  CaretRightOutlined,
  CaretDownOutlined
} from "@ant-design/icons";
import lang from "../helper/lang";
import Main from "../components/layout/Main";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import apiPath from "../constants/apiPath";
import moment from "moment";
import { DatePicker } from "antd";

// Add custom styles for placeholder text
const selectStyle = {
  "--ant-select-selection-placeholder-color": "#bfbfbf",
};

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

function DocumentCategoryManager() {
  const { request } = useRequest();
  const requestRef = useRef(request);

  // State management
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState("add"); // add, edit
  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    sort: "newest",
    status: "",
    dateRange: null,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Loading states for different operations
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});


  const [categoryTree, setCategoryTree] = useState([]);
  const [categoryPath, setCategoryPath] = useState([]); 

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const debouncedSearchText = useDebounce(searchText, 500);

  // Fetch document categories
  const fetchCategories = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();

    // Add sort parameter
    if (filters.sort) {
      params.append("sort", filters.sort);
    }

    // Add status parameter
    if (filters.status) {
      params.append("status", filters.status);
    }

    // Add search parameter
    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    // Add date range filter
    if (filters.dateRange?.length === 2) {
      params.append(
        "start_date",
        filters.dateRange[0].format("YYYY-MM-DD")
      );
      params.append(
        "end_date",
        filters.dateRange[1].format("YYYY-MM-DD")
      );
    }

    // Add pagination parameters
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    requestRef.current({
      url: `/api/admin/${apiPath.documentCategories}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);

        if (data?.status === 200 && data?.data) {
          // Handle the response structure based on your API specification
          setCategories(Array.isArray(data.data) ? data.data : []);
          setCategoryTree(data.data);

          // Handle pagination from your API response
          if (data.pagination) {
            setPagination((prev) => ({
              ...prev,
              total: data.pagination.total || 0,
            }));
          }

        } else {
          setCategories([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
          ShowToast(
            data?.message || "Failed to fetch categories",
            Severty.ERROR
          );
        }
      },
      onError: (error) => {
        setLoading(false);
        setCategories([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        ShowToast("Failed to fetch categories", Severty.ERROR);
      },
    });
  }, [filters, debouncedSearchText, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Handlers
  const handleTableChange = (paginationInfo) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const getChildrenByPath = (tree, path) => {
    let current = tree;

    for (const id of path) {
      const found = current.find(c => c.id === id);
      if (!found || !found.children) return [];
      current = found.children;
    }
    return current;
  };

  const handleCategoryChange = (level, value) => {
    const updatedPath = [...categoryPath.slice(0, level), value];
    setCategoryPath(updatedPath);

    // 🔥 set final selected category to form
    form.setFieldsValue({
      child_of: value,
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddCategory = () => {
    setModalType("add");
    setCategoryPath([]);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    // Full path including itself
    const fullPath = findCategoryPath(categoryTree, category.id) || [];

    // ✅ Remove self → keep only parents
    const parentPath = fullPath.slice(0, -1);
    setCategoryPath(parentPath);
    setModalType("edit");
    setModalVisible(true);
    form.setFieldsValue({
      name: category.name,
      status: category.status === "ACTIVE",
      child_of: parentPath[parentPath.length - 1] || null,
    });
  };

  // Create or update category
  const handleFormSubmit = async (values) => {
    try {
      const categoryData = {
        name: values.name,
        status: values.status ? "ACTIVE" : "INACTIVE",
        parent: values.child_of || null,
      };

      if (modalType === "add") {
        setCreateLoading(true);
        requestRef.current({
          url: `/api/admin/${apiPath.documentCategories}`,
          method: "POST",
          data: categoryData,
          onSuccess: (data) => {
            if (data?.status === 201) {
              ShowToast("Category created successfully", Severty.SUCCESS);
              setModalVisible(false);
              fetchCategories();
            } else {
              ShowToast(
                data?.message || "Failed to create category",
                Severty.ERROR
              );
            }
            setCreateLoading(false);
          },
          onError: (error) => {
            ShowToast(
              error?.response?.data?.message || "Failed to create category",
              Severty.ERROR
            );
            setCreateLoading(false);
          },
        });
      } else {
        setUpdateLoading(true);
        requestRef.current({
          url: `/api/admin/${apiPath.documentCategories}/${selectedCategory.id}`,
          method: "PUT",
          data: categoryData,
          onSuccess: (data) => {
            if (data?.status === 200) {
              ShowToast("Category updated successfully", Severty.SUCCESS);
              setModalVisible(false);
              fetchCategories();
            } else {
              ShowToast(
                data?.message || "Failed to update category",
                Severty.ERROR
              );
            }
            setUpdateLoading(false);
          },
          onError: (error) => {
            ShowToast(
              error?.response?.data?.message || "Failed to update category",
              Severty.ERROR
            );
            setUpdateLoading(false);
          },
        });
      }
    } catch (error) {
      ShowToast("Failed to save category", Severty.ERROR);
      setCreateLoading(false);
      setUpdateLoading(false);
    }
  };

  const findCategoryPath = (tree, targetId, path = []) => {
    for (const node of tree) {
      const newPath = [...path, node.id];

      if (node.id === targetId) {
        return newPath;
      }

      if (node.children && node.children.length > 0) {
        const result = findCategoryPath(node.children, targetId, newPath);
        if (result) return result;
      }
    }
    return null;
  };

  const handleDeleteCategory = async (category) => {
    try {
      setDeleteLoading((prev) => ({ ...prev, [category.id]: true }));
      requestRef.current({
        url: `/api/admin/${apiPath.documentCategories}/${category.id}`,
        method: "DELETE",
        onSuccess: (data) => {
          ShowToast("Category deleted successfully", Severty.SUCCESS);
          setDeleteLoading((prev) => ({ ...prev, [category.id]: false }));
          fetchCategories();
        },
        onError: () => {
          ShowToast("Failed to delete category", Severty.ERROR);
          setDeleteLoading((prev) => ({ ...prev, [category.id]: false }));
        },
      });
    } catch (error) {
      ShowToast("Failed to delete category", Severty.ERROR);
      setDeleteLoading((prev) => ({ ...prev, [category.id]: false }));
    }
  };

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id) => <Text code>CATEGORY_{id}</Text>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
       width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={status === "ACTIVE" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      render: (date) => date ? moment(date).format("YYYY-MM-DD HH:mm A") : "N/A",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 200,
      render: (date) => date ? moment(date).format("YYYY-MM-DD HH:mm A") : "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditCategory(record)}
              disabled={deleteLoading[record.id]}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              danger
              loading={deleteLoading[record.id]}
              onClick={() => {
                Modal.confirm({
                  title: "Delete Category",
                  content: "Are you sure you want to delete this category?",
                  okText: "Yes",
                  okType: "danger",
                  cancelText: "No",
                  onOk: () => handleDeleteCategory(record),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <>
            <span>Document Category Manager</span>
            <p>Manage document categories for the platform</p>
          </>
        }
        extra={
          <Space wrap>
            <Search
              placeholder="Search categories"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Sort By"
              style={{ width: 120, ...selectStyle }}
              value={filters.sort}
              onChange={(value) => handleFilterChange("sort", value)}
            >
              <Option value="newest">Newest</Option>
              <Option value="oldest">Oldest</Option>
            </Select>
            <RangePicker
              style={{ width: 300 }}
              placeholder={[
                lang("Start_Date") || "Start Date",
                lang("End_Date") || "End Date",
              ]}
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange("dateRange", dates)}
              allowClear
            />
            <Select
              placeholder="Filter by Status"
              style={{ width: 150, ...selectStyle }}
              value={filters.status || undefined}
              onChange={(value) => handleFilterChange("status", value)}
              allowClear
            >
              <Option value="ACTIVE">Active</Option>
              <Option value="INACTIVE">Inactive</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
            >
              Add Category
            </Button>
          </Space>
        }
      >
        {/* Categories Table */}
        <div className="table-responsive">
        <Table
          columns={getColumns()}
          dataSource={categories}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total || 0,
            showSizeChanger: true,
            showQuickJumper: false,
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} categories`,
            pageSizeOptions: ["10", "20", "30", "50"],
          }}
          loading={loading}
          onChange={handleTableChange}
          expandable={{
            childrenColumnName: "children",

            // 🔥 THIS decides if row can expand
            rowExpandable: (record) =>
              Array.isArray(record.children) && record.children.length > 0,

            // 🔥 THIS decides if + icon is rendered at all
            expandIcon: ({ expanded, onExpand, record }) =>
              Array.isArray(record.children) && record.children.length > 0 ? (
                <span onClick={(e) => onExpand(record, e)} style={{ marginRight: 8 }}>
                  {expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </span>
              ) : null,
          }}
        />

        </div>
      </Card>

      {/* Add/Edit Category Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {modalType === "add" ? "Add Category" : "Edit Category"}
          </div>
        }
        open={modalVisible}
        onCancel={() =>
          !createLoading && !updateLoading && setModalVisible(false)
        }
        footer={null}
        width={500}
        confirmLoading={createLoading || updateLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          {(modalType === "add"
            ? [categoryTree, ...categoryPath.map((_, i) =>
                getChildrenByPath(categoryTree, categoryPath.slice(0, i + 1))
              )]
            : categoryPath.map((_, i) =>
                getChildrenByPath(categoryTree, categoryPath.slice(0, i))
              )
          ).map((options, index) =>
            options.length > 0 ? (
              <Form.Item
                key={index}
                label={index === 0 ? "Parent Category" : "Sub Category"}
              >
                <Select
                  placeholder="Select category"
                  value={categoryPath[index]}
                  onChange={(value) => handleCategoryChange(index, value)}
                  allowClear={modalType === "add"}        // ❌ no clear in edit
                  disabled={modalType === "edit"}         // 🔥 KEY FIX
                >
                  {options.map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null
          )}
          <Form.Item
            name="name"
            label="Category Name"
            rules={[
              {
                required: true,
                message: "Please enter category name",
              },
              {
                pattern: /^[A-Za-z0-9 _-]+$/,
                message: "Special characters are not allowed",
              },
              {
                max: 100,
                message: "Name must be 100 characters or less",
              },
            ]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item name="child_of" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => setModalVisible(false)}
              disabled={createLoading || updateLoading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={modalType === "add" ? createLoading : updateLoading}
              disabled={createLoading || updateLoading}
            >
              {modalType === "add" ? "Create" : "Update"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default DocumentCategoryManager;
